import { ChangeDetectionStrategy, Component, OnInit, HostBinding, OnDestroy, inject } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { MessageService } from '../../services/message/message.service';
import { StepType } from '../../logic/step-type';
import { Chunk } from '../../interfaces/step/step.interface';
import { AutoUnsubscribe } from '../../decorators/auto-unsubscribe.decorator';

import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { FlexLayoutModule } from '@ngbracket/ngx-layout';
import { CodeEditorComponent } from '../code-editor/code-editor.component';

@AutoUnsubscribe()
@Component({
  standalone: true,
  selector: 'app-chunk-item-description',
  templateUrl: './chunk-item-description.component.html',
  styleUrls: ['./chunk-item-description.component.scss'],
  imports: [
    MatDialogModule,
    MatIconModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatCardModule,
    MatTooltipModule,
    MatSelectModule,
    FlexLayoutModule,
    ReactiveFormsModule,
    FormsModule,
    CodeEditorComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChunkItemInfoComponent implements OnInit, OnDestroy {
  data = inject(MAT_DIALOG_DATA);
  dialogRef = inject<MatDialogRef<ChunkItemInfoComponent>>(MatDialogRef);
  private _formBuilder = inject(FormBuilder);
  private messageService = inject(MessageService);

  chunkItemInfoForm!: FormGroup;
  stepType!: StepType;
  chunkData: Partial<Chunk> = {};
  @HostBinding('class.fullscreen') isFullscreen: boolean = false;

  ngOnInit() {
    const { dialogData } = this.data;

    this.chunkData = dialogData.chunkData;

    this.initForm();
  }

  // eslint-disable-next-line
  ngOnDestroy() {
    // intentionally empty
  }

  private initForm() {
    this.chunkItemInfoForm = this._formBuilder.group({
      description: [this.chunkData.description ?? ''],
    });
  }

  private resetForm() {
    this.chunkItemInfoForm.reset();
    this.resetFormControls();
  }

  private resetFormControls(): void {
    Object.values(this.chunkItemInfoForm.controls).forEach((control) => {
      control.setErrors(null);
      control.markAsUntouched();
      control.markAsPristine();
    });
  }

  public submitChunkInfoForm(value: any, isValid: boolean, $event: any) {
    $event.preventDefault();

    if (!isValid) {
      return;
    }

    const updatedChunk = {
      ...this.chunkData,
      description: value.description,
    };

    this.resetForm();
    this.dialogRef.close(updatedChunk);
  }

  public setFormTouched() {
    this.chunkItemInfoForm.markAsTouched();
  }

  public updateContent(data: string) {
    this.chunkItemInfoForm.get('description')?.setValue(data);
    this.markAllControlsAsDirty();
  }

  private markAllControlsAsDirty(): void {
    Object.keys(this.chunkItemInfoForm.controls).forEach((key) => {
      this.chunkItemInfoForm.controls[key].markAsDirty();
    });
  }

  toggleFullscreen() {
    this.isFullscreen = !this.isFullscreen;
  }
}
